import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, RefreshCw, FileText, Plus, X, Download, Filter } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { useOrganization } from '../hooks/useOrganization';
import { formatDate } from '../utils/dateFormat';

const SaleReturn = () => {
  const { organization } = useOrganization();
  const [returns, setReturns] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    invoiceId: '',
    customerId: '',
    itemDescription: '',
    quantity: 1,
    unitPrice: 0,
    totalAmount: 0,
    costPrice: 0,
    reason: '',
    reasonDescription: '',
    notes: ''
  });
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [viewReturn, setViewReturn] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pages: 1,
    total: 0,
    limit: 25
  });
  const [pageSize, setPageSize] = useState(25);
  const [searchDebounce, setSearchDebounce] = useState(null);

  const reasonOptions = [
    { value: 'DEFECTIVE', label: 'Defective part' },
    { value: 'WRONG_ITEM', label: 'Wrong item' },
    { value: 'CUSTOMER_CHANGE', label: 'Customer change of mind' },
    { value: 'SERVICE_UNSATISFACTORY', label: 'Service not satisfactory' },
    { value: 'WARRANTY_CLAIM', label: 'Warranty claim' },
    { value: 'OTHER', label: 'Other' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'CANCELLED', label: 'Cancelled' }
  ];

  const fetchReturns = useCallback(async (page = 1, limit = pageSize) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit === 500 ? 500 : limit); // Handle "Show All" option
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`/api/returns/sales?${params}`);
      setReturns(response.data.returns || []);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination,
        limit
      }));
    } catch (error) {
      toast.error('Failed to fetch returns');
      setReturns([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, pageSize]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/invoices');
      setInvoices(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
      toast.error('Failed to fetch customers');
    }
  };

  useEffect(() => {
    fetchReturns(1, pageSize);
    fetchInvoices();
    fetchCustomers();
  }, [pageSize]);

  useEffect(() => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    
    const delayedSearch = setTimeout(() => {
      fetchReturns(1); // Always reset to first page on search/filter
    }, 300);
    
    setSearchDebounce(delayedSearch);

    return () => {
      if (delayedSearch) clearTimeout(delayedSearch);
    };
  }, [searchQuery, statusFilter, fetchReturns]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED': return 'bg-blue-100 text-blue-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusUpdate = async (id, newStatus, currentStatus) => {
    if (newStatus === currentStatus) return;
    
    // Validate status transitions
    const validTransitions = {
      'PENDING': ['APPROVED', 'REJECTED', 'CANCELLED'],
      'APPROVED': ['COMPLETED', 'CANCELLED'],
      'COMPLETED': [],
      'REJECTED': ['PENDING'],
      'CANCELLED': []
    };
    
    if (!validTransitions[currentStatus]?.includes(newStatus)) {
      toast.error(`Cannot change status from ${currentStatus} to ${newStatus}`);
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(`/api/returns/sales/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchReturns(pagination.currentPage);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    // Skip itemDescription validation if we have return items from invoice
    if (returnItems.length === 0 && !formData.itemDescription.trim()) {
      toast.error('Item description is required');
      return false;
    }
    if (returnItems.length === 0 && formData.quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return false;
    }
    if (returnItems.length === 0 && formData.unitPrice < 0) {
      toast.error('Unit price cannot be negative');
      return false;
    }
    if (returnItems.length === 0 && formData.totalAmount <= 0) {
      toast.error('Total amount must be greater than 0');
      return false;
    }
    if (!formData.reason) {
      toast.error('Return reason is required');
      return false;
    }
    return true;
  };

  const handleCreateReturn = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    // Check for duplicates before creating
    if (formData.invoiceId) {
      try {
        const itemsToCheck = returnItems.length > 0 
          ? returnItems.map(item => ({ description: item.description }))
          : [{ description: formData.itemDescription }];
          
        const duplicateCheck = await axios.post('/api/returns/sales/check-duplicates', {
          invoiceId: formData.invoiceId,
          items: itemsToCheck
        });
        
        if (duplicateCheck.data.hasIssues) {
          const issueItems = duplicateCheck.data.issues.map(d => 
            `${d.itemDescription}: requested ${d.requestedQuantity}, available ${d.availableQuantity}`
          ).join('; ');
          toast.error(`Cannot create return. Insufficient quantities: ${issueItems}`);
          return;
        }
      } catch (error) {
        toast.error('Failed to validate return. Please try again.');
        return;
      }
    }
    
    setLoading(true);

    try {
      if (returnItems.length > 0) {
        // Bulk create for multiple items
        const response = await axios.post('/api/returns/sales/bulk', {
          returns: returnItems.map(item => ({
            invoiceId: formData.invoiceId || null,
            customerId: formData.customerId || null,
            itemDescription: item.description,
            quantity: item.returnQuantity,
            unitPrice: item.unitPrice,
            totalAmount: item.returnAmount,
            costPrice: item.costPrice || 0,
            reason: formData.reason,
            reasonDescription: formData.reasonDescription,
            notes: formData.notes
          }))
        });
        
        if (response.data.created > 0) {
          toast.success(`${response.data.created} return(s) created successfully`);
        }
        if (response.data.errors > 0) {
          const errorMsg = response.data.errorDetails.map(err => 
            `${err.item}: ${err.error}`
          ).join('; ');
          toast.error(`${response.data.errors} item(s) failed: ${errorMsg}`);
        }
      } else {
        // Single return creation
        await axios.post('/api/returns/sales', formData);
        toast.success('Return created successfully');
      }
      
      setShowCreateForm(false);
      resetForm();
      fetchReturns(pagination.currentPage);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create return');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      invoiceId: '',
      customerId: '',
      itemDescription: '',
      quantity: 1,
      unitPrice: 0,
      totalAmount: 0,
      costPrice: 0,
      reason: '',
      reasonDescription: '',
      notes: ''
    });
    setSelectedInvoice(null);
    setReturnItems([]);
  };

  const handleInvoiceSelect = async (invoiceId) => {
    const invoice = invoices.find(inv => inv.id === parseInt(invoiceId));
    setSelectedInvoice(invoice);
    setFormData(prev => ({
      ...prev,
      invoiceId,
      customerId: invoice?.customerId || ''
    }));
    setReturnItems([]);
    
    // Check for returnable items
    if (invoiceId) {
      try {
        const response = await axios.get(`/api/returns/sales/invoice/${invoiceId}/returnable`);
        if (response.data.existingReturns.length > 0) {
          const returnNumbers = response.data.existingReturns.map(r => r.returnNumber).join(', ');
          toast.info(`This invoice already has returns: ${returnNumbers}`);
        }
      } catch (error) {
      }
    }
  };

  const calculatePriceAutomatically = async (itemDescription, quantity = 1, invoiceId = null, partId = null) => {
    try {
      const response = await axios.post('/api/returns/sales/calculate-price', {
        itemDescription,
        quantity,
        invoiceId,
        partId,
        method: 'ORIGINAL_SALE'
      });
      
      return response.data;
    } catch (error) {
      toast.error('Failed to calculate price automatically');
      return null;
    }
  };

  const handleAutoCalculatePrice = async () => {
    if (!formData.itemDescription.trim()) {
      toast.error('Please enter item description first');
      return;
    }
    
    setLoading(true);
    try {
      const priceResult = await calculatePriceAutomatically(
        formData.itemDescription,
        formData.quantity,
        formData.invoiceId,
        formData.partId
      );
      
      if (priceResult) {
        setFormData(prev => ({
          ...prev,
          unitPrice: priceResult.unitPrice,
          costPrice: priceResult.costPrice,
          totalAmount: priceResult.totalAmount
        }));
        
        toast.success(`Price calculated using ${priceResult.method}`);
      }
    } catch (error) {
      toast.error('Failed to calculate price');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalAmount = (quantity, unitPrice) => {
    return (parseFloat(quantity) * parseFloat(unitPrice)).toFixed(2);
  };

  const handleQuantityChange = (value) => {
    const quantity = parseInt(value) || 0;
    const totalAmount = calculateTotalAmount(quantity, formData.unitPrice);
    setFormData(prev => ({
      ...prev,
      quantity,
      totalAmount: parseFloat(totalAmount)
    }));
  };

  const handleUnitPriceChange = (value) => {
    const unitPrice = parseFloat(value) || 0;
    const totalAmount = calculateTotalAmount(formData.quantity, unitPrice);
    setFormData(prev => ({
      ...prev,
      unitPrice,
      totalAmount: parseFloat(totalAmount)
    }));
  };

  const addReturnItem = async (item, returnQuantity) => {
    if (returnQuantity > item.quantity) {
      toast.error(`Cannot return ${returnQuantity} items. Only ${item.quantity} available.`);
      return;
    }
    
    // Check for existing returns for this item
    if (formData.invoiceId) {
      try {
        const response = await axios.post('/api/returns/sales/check-duplicates', {
          invoiceId: formData.invoiceId,
          items: [{ description: item.description }]
        });
        
        if (response.data.hasIssues) {
          const issue = response.data.issues[0];
          toast.error(`Cannot return ${returnQuantity}. Available quantity: ${issue.availableQuantity}`);
          return;
        }
      } catch (error) {
      }
    }
    
    // Check if item already exists in return list
    const existingItem = returnItems.find(ri => ri.description === item.description);
    if (existingItem) {
      const totalReturning = existingItem.returnQuantity + returnQuantity;
      if (totalReturning > item.quantity) {
        toast.error(`Cannot return ${totalReturning} items. Only ${item.quantity} available.`);
        return;
      }
      // Update existing item
      setReturnItems(prev => prev.map(ri => 
        ri.description === item.description 
          ? { ...ri, returnQuantity: totalReturning, returnAmount: totalReturning * item.price }
          : ri
      ));
    } else {
      // Add new item
      const returnAmount = returnQuantity * item.price;
      const newReturnItem = {
        itemIndex: returnItems.length,
        description: item.description,
        returnQuantity,
        returnAmount,
        originalQuantity: item.quantity,
        unitPrice: item.price,
        costPrice: item.costPrice || 0
      };
      setReturnItems(prev => [...prev, newReturnItem]);
    }
  };

  const removeReturnItem = (index) => {
    setReturnItems(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Sale Returns</h1>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            Total: {pagination.total} returns
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Return
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search returns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex space-x-2">
            <select
              value={pageSize}
              onChange={(e) => {
                const newSize = parseInt(e.target.value);
                setPageSize(newSize);
                setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to page 1
                fetchReturns(1, newSize);
              }}
              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
              <option value={500}>Show All (500)</option>
            </select>
            <button
              onClick={() => fetchReturns(pagination.currentPage)}
              className="flex items-center px-3 py-2 border rounded-md hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button
              className="flex items-center px-3 py-2 border rounded-md hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Returns Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse space-y-4">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
            <p className="text-gray-500 mt-4">Loading returns...</p>
          </div>
        ) : returns.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No returns found</p>
            {(searchQuery || statusFilter) ? (
              <div className="mt-4">
                <p className="text-sm text-gray-400 mb-2">Try adjusting your search or filters</p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400 mt-2">Create your first return to get started</p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">Return #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px] max-w-[200px]">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] max-w-[300px]">Item Description</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">Qty</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] max-w-[150px]">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {returns.map((returnItem) => (
                    <tr key={returnItem.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-4 text-sm font-medium text-blue-600 hover:text-blue-800">
                        <span className="cursor-pointer" onClick={() => setViewReturn(returnItem)} title="Click to view details">
                          {returnItem.returnNumber}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {formatDate(returnItem.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div 
                          className="truncate max-w-[200px]" 
                          title={returnItem.customer?.name || returnItem.jobCard?.customer?.name || 'No customer assigned'}
                        >
                          {returnItem.customer?.name || returnItem.jobCard?.customer?.name || (
                            <span className="text-gray-400 italic">No customer</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div 
                          className="truncate max-w-[300px]" 
                          title={returnItem.itemDescription || 'No description'}
                        >
                          {returnItem.itemDescription || (
                            <span className="text-gray-400 italic">No description</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                        {returnItem.quantity}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 font-medium">
                        {formatCurrency(returnItem.totalAmount || 0, organization?.currency_code)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div 
                          className="truncate max-w-[150px]" 
                          title={reasonOptions.find(r => r.value === returnItem.reason)?.label || returnItem.reason}
                        >
                          {reasonOptions.find(r => r.value === returnItem.reason)?.label || returnItem.reason}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(returnItem.status)}`}>
                          {returnItem.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setViewReturn(returnItem)}
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-150"
                            title="View Details"
                          >
                            👁️
                          </button>
                          <select
                            value={returnItem.status}
                            onChange={(e) => handleStatusUpdate(returnItem.id, e.target.value, returnItem.status)}
                            className="text-xs px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            disabled={loading || returnItem.status === 'COMPLETED' || returnItem.status === 'CANCELLED'}
                          >
                            <option value="PENDING">Pending</option>
                            <option value="APPROVED">Approved</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="CANCELLED">Cancelled</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Enhanced Pagination Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white sticky bottom-0 z-10 shadow-lg">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700 font-medium">
                    Showing <span className="font-semibold">{((pagination.currentPage - 1) * pagination.limit) + 1}</span> to{' '}
                    <span className="font-semibold">{Math.min(pagination.currentPage * pagination.limit, pagination.total)}</span> of{' '}
                    <span className="font-semibold">{pagination.total}</span> results
                  </div>
                  {pagination.total > 0 && (
                    <div className="text-sm text-gray-500">
                      Page {pagination.currentPage} of {pagination.pages}
                    </div>
                  )}
                </div>
                
                {pagination.pages > 1 && (
                  <div className="flex items-center space-x-1">
                    {/* First page button */}
                    <button
                      onClick={() => fetchReturns(1)}
                      disabled={pagination.currentPage === 1 || loading}
                      className="px-2 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors duration-150 text-sm font-medium"
                      title="First page"
                    >
                      ««
                    </button>
                    
                    {/* Previous button */}
                    <button
                      onClick={() => fetchReturns(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1 || loading}
                      className="px-3 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors duration-150 text-sm font-medium"
                    >
                      Previous
                    </button>
                    
                    {/* Smart page numbers */}
                    <div className="flex space-x-1">
                      {(() => {
                        const pages = [];
                        const totalPages = pagination.pages;
                        const current = pagination.currentPage;
                        
                        if (totalPages <= 7) {
                          // Show all pages if 7 or fewer
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Smart pagination for more than 7 pages
                          if (current <= 4) {
                            // Near the beginning
                            for (let i = 1; i <= 5; i++) pages.push(i);
                            pages.push('...');
                            pages.push(totalPages);
                          } else if (current >= totalPages - 3) {
                            // Near the end
                            pages.push(1);
                            pages.push('...');
                            for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                          } else {
                            // In the middle
                            pages.push(1);
                            pages.push('...');
                            for (let i = current - 1; i <= current + 1; i++) pages.push(i);
                            pages.push('...');
                            pages.push(totalPages);
                          }
                        }
                        
                        return pages.map((page, index) => {
                          if (page === '...') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">
                                ...
                              </span>
                            );
                          }
                          
                          return (
                            <button
                              key={page}
                              onClick={() => fetchReturns(page)}
                              disabled={loading}
                              className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors duration-150 ${
                                page === current
                                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                  : 'hover:bg-gray-100 disabled:opacity-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()
                      }
                    </div>
                    
                    {/* Next button */}
                    <button
                      onClick={() => fetchReturns(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.pages || loading}
                      className="px-3 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors duration-150 text-sm font-medium"
                    >
                      Next
                    </button>
                    
                    {/* Last page button */}
                    <button
                      onClick={() => fetchReturns(pagination.pages)}
                      disabled={pagination.currentPage === pagination.pages || loading}
                      className="px-2 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors duration-150 text-sm font-medium"
                      title="Last page"
                    >
                      »»
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Return Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Create Sale Return</h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateReturn} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice (Optional)
                  </label>
                  <select
                    value={formData.invoiceId}
                    onChange={(e) => handleInvoiceSelect(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Invoice</option>
                    {invoices.map(invoice => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.invoicenumber || invoice.invoiceNumber} - {invoice.customer?.name} ({formatCurrency(invoice.total, organization?.currency_code)})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer (Optional)
                  </label>
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerId: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name} - {customer.contact}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Invoice Items */}
              {selectedInvoice && selectedInvoice.items && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Items
                  </label>
                  <div className="border rounded-md max-h-60 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Qty</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Price</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Return Qty</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(selectedInvoice.items || []).filter(item => 
                          !item.description.toLowerCase().includes('service') &&
                          !item.description.toLowerCase().includes('labor')
                        ).map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm">{item.description}</td>
                            <td className="px-3 py-2 text-sm">{item.quantity}</td>
                            <td className="px-3 py-2 text-sm">{formatCurrency(item.price, organization?.currency_code)}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="1"
                                max={item.quantity}
                                className="w-16 px-2 py-1 border rounded text-sm"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    const qty = parseInt(e.target.value) || 0;
                                    if (qty > 0 && qty <= item.quantity) {
                                      addReturnItem(item, qty);
                                      e.target.value = '';
                                    }
                                  }
                                }}
                                onBlur={(e) => {
                                  const qty = parseInt(e.target.value) || 0;
                                  if (qty > 0 && qty <= item.quantity) {
                                    addReturnItem(item, qty);
                                    e.target.value = '';
                                  }
                                }}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => addReturnItem(item, item.quantity)}
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                              >
                                Add All
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Return Items List */}
              {returnItems.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Items to Return ({returnItems.length})
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {returnItems.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1">
                          <span className="text-sm font-medium">{item.description}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            Qty: {item.returnQuantity} × {formatCurrency(item.unitPrice, organization?.currency_code)} = {formatCurrency(item.returnAmount, organization?.currency_code)}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeReturnItem(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Return Form */}
              {returnItems.length === 0 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item Description *
                    </label>
                    <input
                      type="text"
                      value={formData.itemDescription}
                      onChange={(e) => setFormData(prev => ({ ...prev, itemDescription: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Price *
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.unitPrice}
                          onChange={(e) => handleUnitPriceChange(e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={handleAutoCalculatePrice}
                          disabled={loading || !formData.itemDescription.trim()}
                          className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
                          title="Auto-calculate price"
                        >
                          Auto
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Total Amount *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.totalAmount}
                        onChange={(e) => setFormData(prev => ({ ...prev, totalAmount: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cost Price (Optional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.costPrice}
                      onChange={(e) => setFormData(prev => ({ ...prev, costPrice: parseFloat(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Return Reason *
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select reason</option>
                  {reasonOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason Description
                </label>
                <textarea
                  value={formData.reasonDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, reasonDescription: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : `Create Return${returnItems.length > 0 ? ` (${returnItems.length} items)` : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Return Modal */}
      {viewReturn && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Return Details</h3>
              <button
                onClick={() => setViewReturn(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Return Number</label>
                  <p className="text-sm text-gray-900">{viewReturn.returnNumber}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <p className="text-sm text-gray-900">{formatDate(viewReturn.createdAt)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Customer</label>
                  <p className="text-sm text-gray-900">{viewReturn.customer?.name || viewReturn.jobCard?.customer?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Invoice</label>
                  <p className="text-sm text-gray-900">{viewReturn.invoice?.invoicenumber || 'N/A'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Item Description</label>
                <p className="text-sm text-gray-900">{viewReturn.itemDescription}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Quantity</label>
                  <p className="text-sm text-gray-900">{viewReturn.quantity}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Unit Price</label>
                  <p className="text-sm text-gray-900">{formatCurrency(viewReturn.unitPrice, organization?.currency_code)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Total Amount</label>
                  <p className="text-sm text-gray-900">{formatCurrency(viewReturn.totalAmount, organization?.currency_code)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason</label>
                  <p className="text-sm text-gray-900">
                    {reasonOptions.find(r => r.value === viewReturn.reason)?.label || viewReturn.reason}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(viewReturn.status)}`}>
                    {viewReturn.status}
                  </span>
                </div>
              </div>

              {viewReturn.reasonDescription && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reason Description</label>
                  <p className="text-sm text-gray-900">{viewReturn.reasonDescription}</p>
                </div>
              )}

              {viewReturn.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <p className="text-sm text-gray-900">{viewReturn.notes}</p>
                </div>
              )}

              {viewReturn.refundAmount > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Refund Amount</label>
                    <p className="text-sm text-gray-900">{formatCurrency(viewReturn.refundAmount, organization?.currency_code)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Refund Method</label>
                    <p className="text-sm text-gray-900">{viewReturn.refundMethod || 'N/A'}</p>
                  </div>
                </div>
              )}

              {(viewReturn.processor || viewReturn.approver) && (
                <div className="grid grid-cols-2 gap-4">
                  {viewReturn.processor && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Processed By</label>
                      <p className="text-sm text-gray-900">
                        {viewReturn.processor.name}
                        {viewReturn.processedAt && (
                          <span className="text-gray-500 ml-2">
                            ({formatDate(viewReturn.processedAt)})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  {viewReturn.approver && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Approved By</label>
                      <p className="text-sm text-gray-900">
                        {viewReturn.approver.name}
                        {viewReturn.approvedAt && (
                          <span className="text-gray-500 ml-2">
                            ({formatDate(viewReturn.approvedAt)})
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setViewReturn(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleReturn;
